import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { HttpService } from "../../../service/HttpService";
import { DataGrid } from "@mui/x-data-grid";
import { gridClasses } from "@mui/x-data-grid/constants";
import dayjs from "dayjs";

const baseStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  p: 4,
  width: "90%",
  maxWidth: "90%",
  maxHeight: "80vh", // vertical scroll limit
  overflowY: "auto",
};

const labelStyle = {
  fontWeight: 500,
  fontSize: "16px",
  marginBottom: "4px",
};

const inputStyle = {
  background: "#f7f9fc",
  borderRadius: "6px",
};

function ViewWeightRootedModal({ open, handleClose, scheduleId }) {
  const httpService = new HttpService();
  const [details, setDetails] = useState([]);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const fetchWeightRootedData = async () => {
    try {
      const res = await httpService.get(`rockwool/weight-rooted/${scheduleId}`);
      setDetails(res?.data || []);
    } catch (e) {
      setDetails([]);
    }
  };

  useEffect(() => {
    if (open) {
      setTab(0);
      fetchWeightRootedData();
    }
  }, [open]);

  const groupByTabType = (data) => {
    return data.reduce((acc, item) => {
      const key = item.tab_type || "Unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  };

  const groupedData = details && groupByTabType(details);

  // Define custom order + labels dynamically based on whether "zerotoseven" has data
  const hasZeroToSevenData = Array.isArray(details) && details.some((row) => row.tab_type === "zerotoseven");
  const tabOrder = hasZeroToSevenData
    ? ["zerotoseven", "eighttofourteen", "fifteentotwentyone"]
    : ["eighttofourteen", "fifteentotwentyone"];

  const tabLabels = {
    zerotoseven: "0-7 Days",
    eighttofourteen: "8-14 Days",
    fifteentotwentyone: "15-21 Days",
  };

  const baseColumns = [
    {
      field: "cultivar_name",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <>
          <div style={{ padding: "10px" }}>{params?.row?.cultivar_name}</div>
        </>
      ),
    },
    { field: "tray_id", headerName: "Tray ID", flex: 1, minWidth: 140 },
    // {
    //   field: "date",
    //   headerName: "Date",
    //   flex: 1,
    //   minWidth: 140,
    //   renderCell: (params) => (
    //     <div style={{ padding: "10px" }}>
    //       {params.row?.date ? dayjs(params.row.date).format("MM-DD-YYYY") : "-"}
    //     </div>
    //   ),
    // },
    {
      field: "before_weight",
      headerName: "Before Weight",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "after_weight",
      headerName: "After Weight",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "rooting_status",
      headerName: "Rooting Status",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "employee_name",
      headerName: "Employee Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <>
          <div style={{ padding: "10px" }}>
            {params.row?.date
              ? dayjs(params.row.date).format("MM-DD-YYYY")
              : "-"}
          </div>
        </>
      ),
    },
  ];

  // Add cluster_id or clone_id depending on tab
  let columns = [...baseColumns];
  if (tabOrder[tab] === "zerotoseven") {
    columns.splice(2, 0, {
      field: "cluster_id",
      headerName: "Cluster ID",
      flex: 1,
      minWidth: 140,
    });
    columns = columns.filter((col) => col.field !== "rooting_status");
  } else {
    columns.splice(2, 0, {
      field: "clone_id",
      headerName: "Clone ID",
      flex: 1,
      minWidth: 140,
    });
    // Remove before/after weight for fifteentotwentyone
    if (tabOrder[tab] === "fifteentotwentyone") {
      columns = columns.filter(
        (col) => col.field !== "before_weight" && col.field !== "after_weight"
      );
    }
  }

  // filter rows by selected tab_type
  const filteredRows = details.filter((row) => row.tab_type === tabOrder[tab]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={baseStyle}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={600}>
            View Weight & Rooted
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          sx={{
            // backgroundColor: "#fffffc",
            "& .MuiTabs-indicator": {
              backgroundColor: "#00A272",
              height: "3px",
            },
            "& .MuiTab-root": {
              position: "relative",
              color: "#A1A2A1",
              fontWeight: 400,
              fontSize: "16px",
              textTransform: "none",
              padding: "12px 20px",
              marginRight: "8px",
              "&:not(.Mui-selected)::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "3px",
                backgroundColor: "#C4C4C4",
              },
              "&.Mui-selected": {
                color: "#00A272 !important",
                fontWeight: 600,
                backgroundColor: "#E6F6F1",
                borderRadius: "4px 4px 0 0",
              },
            },
          }}
        >
          {tabOrder.map((key, i) => (
            <Tab key={i} label={tabLabels[key]} />
          ))}
        </Tabs>

        {/* Tab Content */}
        <div>
          <div
            style={{
              height: `${filteredRows?.length > 0 ? "" : "200px"}`,
              width: "100%",
              marginTop: 50,
            }}
          >
            <DataGrid
              rows={filteredRows}
              getRowId={(r) =>
                r.id ||
                r._id ||
                `${r.tray_id}-${r.cluster_id}-${r.date}-${Math.random()}`
              }
              columns={columns}
              loading={loading}
              // rowCount={rowCount}
              // pageSizeOptions={[15, 50, 100]}
              // paginationModel={{ pageSize, page }}
              // paginationMode="server"
              // onPaginationModelChange={({ page, pageSize }) => {
              //   setPage(page);
              //   setPageSize(pageSize);
              // }}
              pagination={false}
              hideFooter
              getRowHeight={() => "auto"}
              sx={{
                border: "1px solid #E0E0E0",
                [`& .${gridClasses.cell}`]: { py: 0.5 },
              }}
              autoHeight
              showCellVerticalBorder
              showColumnVerticalBorder
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default ViewWeightRootedModal;

{
  /* <Box mt={3}>
          {tabOrder.map(
            (tabType, idx) =>
              tab === idx && (
                <Box key={tabType}>
                  {groupedData[tabType]?.length > 0 ? (
                    groupedData[tabType].map((row, idx2) => (
                      <Box key={idx2} mb={3}>
                        <Box
                          display="flex"
                          flexDirection="row"
                          gap={2}
                          overflowX="auto"
                          pb={1}
                          flexWrap="wrap"
                        >
                          <Box minWidth={200}>
                            <Typography sx={labelStyle}>
                              Cultivar Name
                            </Typography>
                            <TextField
                              value={row.cultivar_name || ""}
                              size="small"
                              fullWidth
                              InputProps={{ readOnly: true, sx: inputStyle }}
                            />
                          </Box>
                          <Box minWidth={200}>
                            <Typography sx={labelStyle}>Tray ID</Typography>
                            <TextField
                              value={row.tray_id || ""}
                              size="small"
                              fullWidth
                              InputProps={{ readOnly: true, sx: inputStyle }}
                            />
                          </Box>
                          {row.cluster_id && (
                            <Box minWidth={200}>
                              <Typography sx={labelStyle}>
                                Cluster ID
                              </Typography>
                              <TextField
                                value={row.cluster_id}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true, sx: inputStyle }}
                              />
                            </Box>
                          )}

                          {row.clone_id && (
                            <Box minWidth={200}>
                              <Typography sx={labelStyle}>Clone ID</Typography>
                              <TextField
                                value={row.clone_id}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true, sx: inputStyle }}
                              />
                            </Box>
                          )}

                          {tabType !== "fifteentotwentyone" && (
                            <>
                              {row.before_weight !== undefined && (
                                <Box minWidth={200}>
                                  <Typography sx={labelStyle}>
                                    Before Weight
                                  </Typography>
                                  <TextField
                                    value={row.before_weight ?? ""}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                      readOnly: true,
                                      sx: inputStyle,
                                    }}
                                  />
                                </Box>
                              )}
                              {row.after_weight !== undefined && (
                                <Box minWidth={200}>
                                  <Typography sx={labelStyle}>
                                    After Weight
                                  </Typography>
                                  <TextField
                                    value={row.after_weight ?? ""}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                      readOnly: true,
                                      sx: inputStyle,
                                    }}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                          {tabType !== "zerotoseven" && row.rooting_status && (
                            <Box minWidth={200}>
                              <Typography sx={labelStyle}>
                                Rooting Status
                              </Typography>
                              <TextField
                                value={row.rooting_status}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true, sx: inputStyle }}
                              />
                            </Box>
                          )}

                          <Box minWidth={200}>
                            <Typography sx={labelStyle}>
                              Employee Name
                            </Typography>
                            <TextField
                              value={row.employee_name || ""}
                              size="small"
                              fullWidth
                              InputProps={{ readOnly: true, sx: inputStyle }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography>No data available.</Typography>
                  )}
                </Box>
              )
          )}
        </Box> */
}
